<template>
  <!-- Animated, self-teaching guide for the telephony call modes. The mock screen
       is styled to MATCH the real confirmation page + auto-dialer overlay
       (teal #19a69d accent, the same orb/ripple, the same card + buttons) so what
       an agent sees here is what they'll see for real. Pure CSS/JS animation. -->
  <div class="tg tel-ui">
    <div class="tg-head">
      <h3 class="tg-title">{{ $t('tg_title') }}</h3>
      <p class="tg-sub">{{ $t('tg_sub') }}</p>
    </div>

    <!-- Scenario tabs -->
    <div class="tg-tabs" role="tablist">
      <button
        v-for="s in scenarios"
        :key="s.key"
        class="tg-tab"
        :class="{ 'tg-tab--on': active === s.key }"
        role="tab"
        :aria-selected="active === s.key"
        @click="select(s.key)"
      >
        <span class="tg-tab-dot" :style="{ background: s.color }"></span>
        {{ $t(s.label) }}
      </button>
    </div>

    <div class="tg-body">
      <!-- Left: the animated screen mock — mirrors the real .ad-stage overlay -->
      <div class="tg-stage-wrap">
        <div class="tg-stage" :class="'tg-tone-' + phase.tone">
          <!-- top bar (mirrors .ad-top) -->
          <div class="tg-top">
            <div class="tg-brand"><span class="tg-dot"></span>{{ $t('tel_callmode_auto') }}</div>
            <span class="tg-chip">{{ $t(phase.badge) }}</span>
          </div>

          <div class="tg-canvas">
            <!-- start screen — the SAME illustration + Start button the agent sees
                 before a campaign / before opening an order -->
            <div v-if="phase.start" class="tg-start">
              <img :src="startImg" alt="" class="tg-start-img" />
              <button class="tg-start-btn">
                {{ phase.start === 'auto' ? $t('tel_ad_start') : $t('start') }}
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
              </button>
            </div>

            <!-- inbound call popup — matches the real GlobalSoftphone inbound pop -->
            <div v-if="phase.inbound" class="tg-inpop">
              <div class="tg-inpop-head">
                <span class="tg-inpop-badge">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 4 4 20M4 8v12h12"/></svg>
                </span>
                <div>
                  <div class="tg-inpop-title">{{ $t('tg_inbound_incoming') }}</div>
                  <div class="tg-inpop-caller" dir="ltr">{{ mock.rawphone }}</div>
                </div>
              </div>
              <div class="tg-inpop-body">
                <div class="tg-inpop-name">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#5743BE" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="10" r="3"/><path d="M6.7 19a6 6 0 0 1 10.6 0"/></svg>
                  <b>{{ mock.name }}</b>
                </div>
                <div class="tg-inpop-meta">
                  <span># {{ mock.orderNoBare }}</span>
                  <span dir="ltr">{{ mock.rawphone }}</span>
                </div>
                <div class="tg-inpop-prod">
                  <span class="tg-inpop-prod-img">🛒</span>
                  <span>{{ mock.product }}</span>
                </div>
                <span class="tg-inpop-open">
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>
                  {{ $t('tel_open_order') }}
                </span>
                <div class="tg-inpop-actions">
                  <button class="tg-inpop-answer">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.05-.24 11.36 11.36 0 0 0 3.56.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.56 1 1 0 0 1-.24 1.05z"/></svg>
                    {{ $t('tg_btn_answer') }}
                  </button>
                  <button class="tg-inpop-reject">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M21 15.46l-5.27-.61-2.52 2.52a15.05 15.05 0 0 1-6.59-6.59l2.53-2.53L8.54 3H3.03A17 17 0 0 0 21 20.97z"/><line x1="1" y1="1" x2="23" y2="23" stroke="#fff" stroke-width="2"/></svg>
                    {{ $t('tg_btn_reject') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- softphone dialing panel — matches the real dark GlobalSoftphone -->
            <div v-if="phase.softphone" class="tg-soft">
              <div class="tg-soft-top">
                <div class="tg-soft-orb">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.35 1.85.58 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                </div>
                <div class="tg-soft-num" dir="ltr">{{ mock.rawphone }}</div>
                <div class="tg-soft-status">{{ $t('tel_calling') }}</div>
              </div>
              <div class="tg-soft-controls">
                <span class="tg-soft-mic">
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0M12 19v3"/></svg>
                </span>
                <span class="tg-soft-hang">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="#fff"><path d="M21 15.46l-5.27-.61-2.52 2.52a15.05 15.05 0 0 1-6.59-6.59l2.53-2.53L8.54 3H3.03A17 17 0 0 0 21 20.97z" transform="rotate(135 12 12)"/></svg>
                </span>
              </div>
            </div>

            <!-- pulsing phone orb (mirrors .ad-orb / .ad-ring / .ad-core) -->
            <div v-if="phase.orb" class="tg-orb" :class="{ 'tg-orb--dial': phase.tone === 'dial' }">
              <span class="tg-ring"></span><span class="tg-ring"></span><span class="tg-ring"></span>
              <div class="tg-core">
                <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.35 1.85.58 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
              </div>
            </div>

            <!-- Multi-agent pool (predictive mode): the server sees the connected
                 agents grouped by their team's sub-queue, and splits the free SIMs
                 proportionally. Shows two teams with different agent counts → the
                 team with more agents gets more lines. -->
            <div v-if="phase.pool" class="tg-pool">
              <div class="tg-pool-head">{{ $t('tg_pool_server') }}</div>
              <div class="tg-pool-teams">
                <div class="tg-pool-team">
                  <div class="tg-pool-team-name">{{ $t('tg_pool_teamA') }}</div>
                  <div class="tg-pool-dots">
                    <span class="tg-pool-dot tg-pool-dot--on"></span>
                    <span class="tg-pool-dot tg-pool-dot--on"></span>
                    <span class="tg-pool-dot tg-pool-dot--on"></span>
                  </div>
                  <div class="tg-pool-lines">{{ $t('tg_pool_lines', { n: 3 }) }}</div>
                </div>
                <div class="tg-pool-team">
                  <div class="tg-pool-team-name">{{ $t('tg_pool_teamB') }}</div>
                  <div class="tg-pool-dots">
                    <span class="tg-pool-dot tg-pool-dot--on"></span>
                  </div>
                  <div class="tg-pool-lines">{{ $t('tg_pool_lines', { n: 1 }) }}</div>
                </div>
              </div>
            </div>

            <!-- order card — mirrors the confirmation-page customer card -->
            <div v-if="phase.card" class="tg-card">
              <div class="tg-card-top">
                <span class="tg-card-icon">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 22v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </span>
                <span class="tg-card-heading">{{ $t('tg_field_client') }}</span>
              </div>
              <div class="tg-card-body">
                <div class="tg-card-idrow">
                  <span class="tg-card-name">{{ mock.name }}</span>
                  <span class="tg-card-oid">{{ mock.orderNo }}</span>
                </div>
                <a class="tg-card-phone" dir="ltr">{{ mock.phone }}</a>
                <div class="tg-card-line"><span>{{ $t('tg_field_product') }}</span><b>{{ mock.product }}</b></div>
                <div class="tg-card-total"><span>{{ $t('tg_field_total') }}</span><b>{{ mock.total }}</b></div>
              </div>
              <div class="tg-card-actions" v-if="phase.showCall">
                <button class="tg-btn-call tg-attract">
                  <span class="tg-btn-call-ic">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.05-.24 11.36 11.36 0 0 0 3.56.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.36 11.36 0 0 0 .57 3.56 1 1 0 0 1-.24 1.05z"/></svg>
                  </span>
                  {{ $t('tg_btn_call') }}
                </button>
              </div>
              <div class="tg-card-bar" v-else-if="phase.talking">
                <button class="tg-abtn tg-abtn--primary">{{ $t('tg_btn_confirm') }}</button>
                <button class="tg-abtn tg-abtn--warning">{{ $t('tg_btn_unreached') }}</button>
                <button class="tg-abtn tg-abtn--danger">{{ $t('tg_btn_cancel') }}</button>
              </div>
            </div>

            <!-- auto-dialer outcome chip (no-answer / voicemail → handled server-side) -->
            <div v-if="phase.result" class="tg-result" :class="'tg-result--' + phase.result">
              <div class="tg-result-ic">
                <svg v-if="phase.result === 'noanswer'" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                <svg v-else viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="7" cy="12" r="2"/><circle cx="17" cy="12" r="2"/></svg>
              </div>
              <div class="tg-result-txt">
                <b>{{ $t(phase.result === 'noanswer' ? 'tg_result_noanswer' : 'tg_result_vm') }}</b>
                <small>{{ $t('tg_result_auto') }}</small>
              </div>
            </div>

            <!-- missed-call: the real floating buttons (bottom-right) + the bell
                 dropdown, exactly like the CRM. -->
            <div v-if="phase.bell" class="tg-mc">
              <!-- dropdown (opens above the bell) -->
              <div class="tg-bell">
                <div class="tg-bell-head">
                  <span>{{ $t('mc_title') }}</span><span class="tg-bell-count">2</span>
                </div>
                <div class="tg-bell-item" :class="{ 'tg-bell-item--claim': phase.claiming }">
                  <div class="tg-bell-ic">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 4 4 20M4 8v12h12"/></svg>
                  </div>
                  <div class="tg-bell-txt">
                    <b>{{ mock.name }}</b>
                    <small dir="ltr">{{ mock.phone }} · {{ mock.orderNo }}</small>
                  </div>
                  <span class="tg-bell-cta">{{ phase.claiming ? $t('mc_claiming') : $t('mc_callback') }}</span>
                </div>
                <div class="tg-bell-item">
                  <div class="tg-bell-ic">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 4 4 20M4 8v12h12"/></svg>
                  </div>
                  <div class="tg-bell-txt">
                    <b>{{ mock.name2 }}</b>
                    <small dir="ltr">{{ mock.phone2 }} · {{ mock.orderNo }}</small>
                  </div>
                  <span class="tg-bell-cta">{{ $t('mc_callback') }}</span>
                </div>
              </div>
              <!-- the 3 floating action buttons (bottom-right stack) -->
              <div class="tg-fabs">
                <div class="tg-fab tg-fab--bell">
                  <span class="tg-fab-badge tg-fab-badge--red">2</span>
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 4 4 20M4 8v12h12"/></svg>
                </div>
                <div class="tg-fab tg-fab--people">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </div>
                <div class="tg-fab tg-fab--stats">
                  <span class="tg-fab-badge tg-fab-badge--green">6</span>
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6"/><rect x="12" y="8" width="3" height="10"/><rect x="17" y="4" width="3" height="14"/></svg>
                </div>
              </div>
            </div>

            <!-- status line (mirrors .ad-status) -->
            <div v-if="phase.orb || phase.result" class="tg-status">
              <p class="tg-st-main" :key="phase.text">{{ $t(phase.text) }}</p>
              <p class="tg-st-sub"><b>{{ mock.name }}</b> · <span dir="ltr">{{ mock.phone }}</span></p>
            </div>

            <!-- progress bar (mirrors .ad-bar), only during active dialing/ringing -->
            <div v-if="phase.orb" class="tg-progress"><i></i></div>
          </div>

        </div>

        <!-- progress dots + play -->
        <div class="tg-steps">
          <span
            v-for="(st, i) in steps"
            :key="i"
            class="tg-step"
            :class="{ 'tg-step--on': i === stepIdx, 'tg-step--done': i < stepIdx }"
          ></span>
        </div>
        <button class="tg-play" @click="toggle">
          <svg v-if="!playing" viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          <svg v-else viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>
          {{ playing ? $t('tg_pause') : $t('tg_play') }}
        </button>
      </div>

      <!-- Right: the written explanation, step-highlighted -->
      <div class="tg-explain">
        <h4 class="tg-explain-title">{{ $t(activeScenario.headline) }}</h4>
        <ol class="tg-list">
          <li
            v-for="(st, i) in steps"
            :key="i"
            class="tg-li"
            :class="{ 'tg-li--on': i === stepIdx }"
          >
            <span class="tg-li-num">{{ i + 1 }}</span>
            <span class="tg-li-txt">{{ $t(st.desc) }}</span>
          </li>
        </ol>
        <p class="tg-note">{{ $t(activeScenario.note) }}</p>
      </div>
    </div>
  </div>
</template>

<script>
// The SAME start-screen illustration the real confirmation page shows, so step 1
// looks exactly like what the agent sees before starting.
import startImg from "@/assets/images/svg/confirmation1.svg";

// Each scenario = an ordered list of steps; a step drives the screen state
// (phase) + the highlighted explanation line. Tones mirror the real overlay:
// dial = teal (#19a69d), ring/answered = green (#16a34a), noanswer/vm = amber.
const SCENARIOS = {
  auto: {
    key: "auto", label: "tel_callmode_auto", color: "#19a69d",
    headline: "tg_auto_headline", note: "tg_auto_note",
    steps: [
      { desc: "tg_auto_s1", phase: { badge: "tg_ph_start", tone: "dial", start: "auto", text: "tg_auto_t1" } },
      { desc: "tg_auto_s2", phase: { badge: "tg_ph_dialing", tone: "dial", orb: true, text: "tg_auto_t2" } },
      { desc: "tg_auto_s3", phase: { badge: "tg_ph_ringing", tone: "green", orb: true, text: "tg_auto_t3" } },
      { desc: "tg_auto_s4", phase: { badge: "tg_ph_answered", tone: "green", card: true, talking: true, text: "tg_auto_t4" } },
      { desc: "tg_auto_s_noanswer", phase: { badge: "tg_ph_noanswer", tone: "amber", result: "noanswer", text: "tg_auto_t_noanswer" } },
      { desc: "tg_auto_s_vm", phase: { badge: "tg_ph_vm", tone: "amber", result: "vm", text: "tg_auto_t_vm" } },
      { desc: "tg_auto_s5", phase: { badge: "tg_ph_next", tone: "dial", orb: true, text: "tg_auto_t5" } },
    ],
  },
  predictive: {
    key: "predictive", label: "tel_callmode_predictive", color: "#0ea5e9",
    headline: "tg_pred_headline", note: "tg_pred_note",
    steps: [
      // Server-driven central queue: the SERVER (not the agent) paces the calls.
      { desc: "tg_pred_s1", phase: { badge: "tg_ph_start", tone: "dial", start: "auto", text: "tg_pred_t1" } },
      // MULTI-AGENT pool: the server counts the connected agents, groups them by
      // their team's sub-queue, and splits the free SIMs PROPORTIONALLY (a team with
      // more connected agents dials more clients). Mirrors _pacingTickForEntry.
      { desc: "tg_pred_s_pool", phase: { badge: "tg_ph_pool", tone: "dial", pool: true, text: "tg_pred_t_pool" } },
      { desc: "tg_pred_s2", phase: { badge: "tg_ph_dialing", tone: "dial", orb: true, pool: true, text: "tg_pred_t2" } },
      { desc: "tg_pred_s3", phase: { badge: "tg_ph_ringing", tone: "green", orb: true, text: "tg_pred_t3" } },
      // Each answered client is routed to ITS OWN team's queue (atomic claim), so a
      // client is only ever bridged to an agent who handles that client's orders.
      { desc: "tg_pred_s_route", phase: { badge: "tg_ph_route", tone: "dial", pool: true, text: "tg_pred_t_route" } },
      // Client answered but NO agent free yet → hold in the queue with the wait
      // message + music-on-hold (the whole point of the mode: never lose the client).
      { desc: "tg_pred_s4", phase: { badge: "tg_ph_queue", tone: "amber", orb: true, text: "tg_pred_t4" } },
      // An agent in that team frees up → the server bridges the waiting client to them.
      { desc: "tg_pred_s5", phase: { badge: "tg_ph_answered", tone: "green", card: true, talking: true, text: "tg_pred_t5" } },
    ],
  },
  manual: {
    key: "manual", label: "tel_callmode_manual", color: "#8b5cf6",
    headline: "tg_manual_headline", note: "tg_manual_note",
    steps: [
      { desc: "tg_manual_s0", phase: { badge: "tg_ph_start", tone: "dial", start: "manual", text: "tg_manual_t0" } },
      { desc: "tg_manual_s1", phase: { badge: "tg_ph_open", tone: "dial", card: true, showCall: true, text: "tg_manual_t1" } },
      { desc: "tg_manual_s2", phase: { badge: "tg_ph_dialing", tone: "dial", softphone: true, text: "tg_manual_t2" } },
      { desc: "tg_manual_s3", phase: { badge: "tg_ph_answered", tone: "green", card: true, talking: true, text: "tg_manual_t3" } },
    ],
  },
  inbound: {
    key: "inbound", label: "tg_tab_inbound", color: "#16a34a",
    headline: "tg_inbound_headline", note: "tg_inbound_note",
    steps: [
      { desc: "tg_inbound_s1", phase: { badge: "tg_ph_incoming", tone: "green", inbound: true, text: "tg_inbound_t1" } },
      { desc: "tg_inbound_s2", phase: { badge: "tg_ph_incoming", tone: "green", inbound: true, text: "tg_inbound_t2" } },
      { desc: "tg_inbound_s3", phase: { badge: "tg_ph_answered", tone: "green", card: true, talking: true, text: "tg_inbound_t3" } },
    ],
  },
  missed: {
    key: "missed", label: "tg_tab_missed", color: "#ef4444",
    headline: "tg_missed_headline", note: "tg_missed_note",
    steps: [
      { desc: "tg_missed_s1", phase: { badge: "tg_ph_missed", tone: "amber", result: "noanswer", text: "tg_missed_t1" } },
      { desc: "tg_missed_s2", phase: { badge: "tg_ph_missed", tone: "amber", bell: true, text: "tg_missed_t2" } },
      { desc: "tg_missed_s3", phase: { badge: "tg_ph_claim", tone: "dial", bell: true, claiming: true, text: "tg_missed_t3" } },
      { desc: "tg_missed_s4", phase: { badge: "tg_ph_answered", tone: "green", card: true, talking: true, text: "tg_missed_t4" } },
    ],
  },
};

export default {
  name: "TelephonyGuide",
  data() {
    return {
      active: "auto",
      stepIdx: 0,
      playing: false,
      startImg,
      _timer: null,
      mock: {
        name: "Ahmed B.", phone: "+212 6 35 41 76 72", rawphone: "0635417672",
        name2: "Youssef K.", phone2: "+212 6 35 41 76 73",
        orderNo: "#MS66ZP", orderNoBare: "MS66ZP", product: "Montre Homme × 1", total: "349 DH",
      },
      scenarios: Object.values(SCENARIOS),
    };
  },
  computed: {
    activeScenario() { return SCENARIOS[this.active]; },
    steps() { return this.activeScenario.steps; },
    phase() { return this.steps[this.stepIdx].phase; },
  },
  mounted() {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reduce) this.play();
  },
  beforeDestroy() { this.stop(); },
  methods: {
    select(key) { this.active = key; this.stepIdx = 0; this.play(); },
    toggle() { this.playing ? this.stop() : this.play(); },
    play() { this.stop(); this.playing = true; this._tick(); },
    _tick() {
      this._timer = setTimeout(() => {
        if (this.stepIdx < this.steps.length - 1) { this.stepIdx++; this._tick(); }
        else { this._timer = setTimeout(() => { this.stepIdx = 0; if (this.playing) this._tick(); }, 1600); }
      }, 2400);
    },
    stop() { this.playing = false; if (this._timer) { clearTimeout(this._timer); this._timer = null; } },
  },
};
</script>

<style scoped>
/* Palette pulled from the REAL confirmation page: teal accent #19a69d, the same
   green/amber semantic tones, slate neutrals. --accent/--accent-soft switch by
   tone exactly like the .ad-stage overlay. */
.tg { --accent:#19a69d; --accent-soft:rgba(25,166,157,.12);
  --slate:#0f172a; --muted:#64748b; --faint:#94a3b8; --line:#e2e8f0; --ground:#f8fafc;
  color:#334155; font-family:inherit; }
.tg-head { margin-bottom: 14px; }
.tg-title { font-size: 15px; font-weight: 700; color: var(--slate); }
.tg-sub { font-size: 12px; color: var(--muted); margin-top: 2px; }

.tg-tabs { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
.tg-tab { display: inline-flex; align-items: center; gap: 7px; padding: 7px 13px;
  font-size: 12.5px; font-weight: 600; color: var(--muted); background: #fff;
  border: 1px solid var(--line); border-radius: 999px; cursor: pointer; transition: all .15s; }
.tg-tab:hover { border-color: #cbd5e1; }
.tg-tab--on { color: var(--slate); border-color: var(--accent); background: var(--accent-soft); }
.tg-tab-dot { width: 8px; height: 8px; border-radius: 999px; }

.tg-body { display: grid; grid-template-columns: minmax(0, 340px) 1fr; gap: 24px; align-items: start; }
@media (max-width: 760px) { .tg-body { grid-template-columns: 1fr; } }

/* ── stage (mirrors .ad-stage) ── */
.tg-stage-wrap { display: flex; flex-direction: column; gap: 12px; }
.tg-stage { position: relative; background: #fff; border: 1px solid var(--line); border-radius: 18px;
  overflow: hidden; min-height: 300px; display: flex; flex-direction: column;
  box-shadow: 0 12px 40px -12px rgba(15,23,42,.18); }
.tg-tone-dial { --accent:#19a69d; --accent-soft:rgba(25,166,157,.12); }
.tg-tone-green { --accent:#16a34a; --accent-soft:rgba(22,163,74,.12); }
.tg-tone-amber { --accent:#d97706; --accent-soft:rgba(217,119,6,.12); }

.tg-top { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid #eef2f7; }
.tg-brand { display: flex; align-items: center; gap: 8px; font-size: 12.5px; font-weight: 600; color: var(--slate); }
.tg-dot { width: 9px; height: 9px; border-radius: 999px; background: var(--accent); box-shadow: 0 0 0 4px var(--accent-soft); animation: tg-live 2s infinite; }
@keyframes tg-live { 0%,100% { opacity: 1; } 50% { opacity: .4; } }
.tg-chip { font-size: 10px; font-weight: 600; letter-spacing: .4px; text-transform: uppercase; color: #fff; background: var(--accent); padding: 4px 10px; border-radius: 999px; }

.tg-canvas { position: relative; flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; padding: 22px 18px; }

/* start screen (mirrors the confirmation page's illustration + Start button) */
.tg-start { display: flex; flex-direction: column; align-items: center; gap: 16px; animation: tg-fade .4s; }
.tg-start-img { width: 150px; max-width: 60%; height: auto; }
.tg-start-btn { display: inline-flex; align-items: center; gap: 8px; font-size: 12.5px; font-weight: 600;
  color: #fff; background: var(--accent); border: none; border-radius: 12px; padding: 9px 20px; cursor: default;
  box-shadow: 0 6px 16px -6px rgba(25,166,157,.5); }

/* orb (mirrors .ad-orb, scaled to fit) */
/* Multi-agent pool panel (predictive mode) */
.tg-pool { width: 100%; max-width: 300px; background: #fff; border: 1px solid var(--line); border-radius: 14px; padding: 12px 14px; box-shadow: 0 6px 18px -12px rgba(15,23,42,.35); }
.tg-pool-head { font-size: 11px; font-weight: 700; letter-spacing: .02em; color: #0369a1; text-transform: uppercase; margin-bottom: 10px; }
.tg-pool-teams { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.tg-pool-team { border: 1px solid var(--line); border-radius: 10px; padding: 9px 10px; background: #f8fafc; }
.tg-pool-team-name { font-size: 12px; font-weight: 600; color: #334155; margin-bottom: 6px; }
.tg-pool-dots { display: flex; gap: 4px; margin-bottom: 6px; min-height: 12px; }
.tg-pool-dot { width: 11px; height: 11px; border-radius: 999px; background: #cbd5e1; }
.tg-pool-dot--on { background: #16a34a; box-shadow: 0 0 0 2px rgba(22,163,74,.15); }
.tg-pool-lines { font-size: 11px; font-weight: 600; color: #0ea5e9; }
.tg-orb { position: relative; width: 116px; height: 116px; display: grid; place-items: center; }
.tg-ring { position: absolute; inset: 0; border-radius: 999px; border: 2px solid var(--accent); opacity: 0; animation: tg-ripple 2.6s ease-out infinite; }
.tg-ring:nth-child(2) { animation-delay: .8s; } .tg-ring:nth-child(3) { animation-delay: 1.6s; }
@keyframes tg-ripple { 0% { transform: scale(.45); opacity: .4; } 80% { opacity: .05; } 100% { transform: scale(1.05); opacity: 0; } }
.tg-core { width: 68px; height: 68px; border-radius: 999px; display: grid; place-items: center;
  background: var(--accent-soft); border: 1px solid var(--accent); color: var(--accent);
  transition: color .4s, background .4s, border-color .4s; }
.tg-orb--dial .tg-core { animation: tg-nudge 1.2s ease-in-out infinite; }
@keyframes tg-nudge { 0%,100% { transform: translateY(0) rotate(0); } 25% { transform: translateY(-3px) rotate(-6deg); } 75% { transform: translateY(2px) rotate(5deg); } }

/* status (mirrors .ad-status) */
.tg-status { text-align: center; }
.tg-st-main { font-size: 15px; font-weight: 600; margin: 0; color: var(--slate); animation: tg-fade .4s; }
.tg-st-sub { margin: 5px 0 0; color: var(--muted); font-size: 11.5px; }
.tg-st-sub b { color: var(--slate); font-weight: 600; }
@keyframes tg-fade { from { opacity: 0; } to { opacity: 1; } }

/* progress bar (mirrors .ad-bar) */
.tg-progress { width: min(240px, 80%); height: 6px; border-radius: 999px; background: #eef2f7; overflow: hidden; border: 1px solid var(--line); }
.tg-progress > i { display: block; height: 100%; width: 38%; border-radius: 999px; background: var(--accent); animation: tg-slide 1.6s ease-in-out infinite; }
@keyframes tg-slide { 0% { transform: translateX(-120%); } 100% { transform: translateX(320%); } }

/* order card (mirrors the confirmation customer card) */
.tg-card { width: 100%; max-width: 280px; background: #fff; border: 1px solid var(--line); border-radius: 14px;
  overflow: hidden; box-shadow: 0 6px 20px -10px rgba(15,23,42,.12); animation: tg-pop .35s ease-out; }
@keyframes tg-pop { from { transform: translateY(8px) scale(.97); opacity: 0; } to { transform: none; opacity: 1; } }
.tg-card-top { display: flex; align-items: center; gap: 8px; padding: 10px 13px; border-bottom: 1px solid #eef2f7; }
.tg-card-icon { width: 26px; height: 26px; border-radius: 8px; background: #FCE4EC; color: #E91E63; display: grid; place-items: center; }
.tg-card-heading { font-size: 12px; font-weight: 600; color: var(--slate); }
.tg-card-body { padding: 13px; }
.tg-card-idrow { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.tg-card-name { font-size: 13.5px; font-weight: 600; color: var(--accent); }
.tg-card-oid { font-size: 11px; font-weight: 600; color: var(--slate); }
.tg-card-phone { display: block; font-size: 12px; font-weight: 500; color: var(--accent); margin-top: 3px; font-variant-numeric: tabular-nums; }
.tg-card-line, .tg-card-total { display: flex; justify-content: space-between; font-size: 11.5px; color: var(--muted); margin-top: 9px; padding-top: 9px; border-top: 1px solid #eef2f7; }
.tg-card-line b { color: var(--slate); font-weight: 600; }
.tg-card-total b { color: var(--accent); font-weight: 700; }
.tg-card-actions { padding: 0 13px 13px; }

/* Call button (mirrors the real green gradient .call-btn) */
.tg-btn-call { width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 10px; border-radius: 12px; border: none; color: #fff; font-size: 12.5px; font-weight: 600;
  background: linear-gradient(to bottom, #22c55e, #16a34a); box-shadow: 0 8px 18px -6px rgba(22,163,74,.4); cursor: default; }
.tg-btn-call-ic { width: 22px; height: 22px; border-radius: 999px; background: rgba(255,255,255,.2); display: grid; place-items: center; }
.tg-attract { animation: tg-callpulse 2s ease-out infinite; }
@keyframes tg-callpulse { 0% { box-shadow: 0 0 0 0 rgba(34,197,94,.5); } 70% { box-shadow: 0 0 0 10px rgba(34,197,94,0); } 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); } }

/* action bar (mirrors the sticky confirm/unreached/cancel bar) */
.tg-card-bar { display: flex; gap: 6px; padding: 11px 13px; border-top: 1px solid #eef2f7; background: #fff; }
.tg-abtn { flex: 1; font-size: 11px; font-weight: 600; padding: 8px 6px; border-radius: 10px; border: 1px solid transparent; color: #fff; cursor: default; }
.tg-abtn--primary { background: #19a69d; }
.tg-abtn--warning { background: #eb5b00; }
.tg-abtn--danger { background: #ef4444; }

/* outcome chip */
.tg-result { display: flex; align-items: center; gap: 12px; width: 100%; max-width: 280px; background: #fff;
  border: 1px solid var(--line); border-radius: 14px; padding: 13px 14px; box-shadow: 0 6px 20px -10px rgba(15,23,42,.12); animation: tg-pop .35s ease-out; }
.tg-result-ic { width: 40px; height: 40px; border-radius: 999px; display: grid; place-items: center; flex-shrink: 0; }
.tg-result--noanswer .tg-result-ic { background: #fee2e2; color: #ef4444; }
.tg-result--vm .tg-result-ic { background: #fef3c7; color: #d97706; }
.tg-result-txt { display: flex; flex-direction: column; }
.tg-result-txt b { font-size: 12.5px; color: var(--slate); }
.tg-result-txt small { font-size: 10.5px; color: var(--muted); margin-top: 1px; }

/* missed-call bell */
.tg-bell { width: 100%; max-width: 280px; background: #fff; border: 1px solid var(--line); border-radius: 14px;
  overflow: hidden; box-shadow: 0 6px 20px -10px rgba(15,23,42,.12); animation: tg-pop .35s ease-out; }
.tg-bell-head { display: flex; justify-content: space-between; align-items: center; padding: 9px 13px; border-bottom: 1px solid #eef2f7; font-size: 12px; font-weight: 700; color: var(--slate); }
.tg-bell-count { font-size: 10px; color: var(--faint); }
.tg-bell-item { display: flex; align-items: center; gap: 9px; padding: 10px 13px; transition: background .2s; }
.tg-bell-item--claim { background: var(--accent-soft); }
.tg-bell-ic { width: 30px; height: 30px; border-radius: 999px; background: #fee2e2; color: #ef4444; display: grid; place-items: center; }
.tg-bell-item--claim .tg-bell-ic { background: var(--accent-soft); color: var(--accent); }
.tg-bell-txt { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.tg-bell-txt b { font-size: 12.5px; color: var(--slate); }
.tg-bell-txt small { font-size: 10.5px; color: var(--muted); font-variant-numeric: tabular-nums; }
.tg-bell-cta { font-size: 10.5px; font-weight: 700; color: var(--accent); }
.tg-bell-item--claim .tg-bell-cta { animation: tg-blink 1s infinite; }
@keyframes tg-blink { 50% { opacity: .3; } }

/* missed-call: dropdown + floating buttons stacked bottom-right (like the CRM) */
.tg-mc { position: relative; width: 100%; display: flex; flex-direction: column; align-items: flex-end; gap: 12px; animation: tg-fade .4s; }
.tg-mc .tg-bell { max-width: 300px; align-self: flex-end; }
.tg-fabs { display: flex; flex-direction: column; align-items: center; gap: 12px; }
.tg-fab { position: relative; width: 46px; height: 46px; border-radius: 999px; background: #fff; border: 2px solid; display: grid; place-items: center; box-shadow: 0 6px 16px -6px rgba(15,23,42,.25); }
.tg-fab--bell { border-color: #ef4444; color: #ef4444; }
.tg-fab--people { border-color: #2563eb; color: #2563eb; }
.tg-fab--stats { border-color: #6366f1; color: #6366f1; }
.tg-fab-badge { position: absolute; top: -6px; right: -6px; min-width: 20px; height: 20px; padding: 0 5px; border-radius: 999px; color: #fff; font-size: 10px; font-weight: 700; display: grid; place-items: center; border: 2px solid #fff; }
.tg-fab-badge--red { background: #ef4444; animation: tg-blink 1.4s infinite; }
.tg-fab-badge--green { background: #22c55e; }

/* inbound call popup — matches the real GlobalSoftphone inbound pop */
.tg-inpop { width: 100%; max-width: 280px; background: #fff; border-radius: 16px; overflow: hidden;
  box-shadow: 0 12px 40px -12px rgba(15,23,42,.28), 0 0 0 1px rgba(0,0,0,.08); animation: tg-pop .35s ease-out; }
.tg-inpop-head { display: flex; align-items: center; gap: 10px; padding: 12px 16px; color: #fff;
  background: linear-gradient(to bottom, #059669, #047857); }
.tg-inpop-badge { width: 34px; height: 34px; border-radius: 999px; background: rgba(255,255,255,.2); display: grid; place-items: center; animation: tg-live 1.5s infinite; }
.tg-inpop-title { font-size: 13px; font-weight: 700; }
.tg-inpop-caller { font-size: 11px; color: rgba(255,255,255,.8); font-variant-numeric: tabular-nums; }
.tg-inpop-body { padding: 13px 16px; }
.tg-inpop-name { display: flex; align-items: center; gap: 7px; }
.tg-inpop-name b { font-size: 13px; font-weight: 700; color: var(--slate); text-transform: uppercase; }
.tg-inpop-meta { display: flex; flex-wrap: wrap; gap: 4px 12px; font-size: 11px; color: var(--muted); margin-top: 5px; font-variant-numeric: tabular-nums; }
.tg-inpop-prod { display: flex; align-items: center; gap: 8px; margin-top: 9px; padding: 7px; background: var(--ground); border-radius: 10px; }
.tg-inpop-prod-img { width: 34px; height: 34px; border-radius: 8px; background: #2d2d2d; display: grid; place-items: center; font-size: 15px; flex-shrink: 0; }
.tg-inpop-prod span:last-child { font-size: 12px; color: #334155; }
.tg-inpop-open { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; color: #5743BE; margin-top: 10px; }
.tg-inpop-actions { display: flex; gap: 8px; margin-top: 14px; }
.tg-inpop-answer, .tg-inpop-reject { display: inline-flex; align-items: center; justify-content: center; gap: 6px; font-size: 12.5px; font-weight: 700; color: #fff; border: none; border-radius: 12px; padding: 10px; cursor: default; }
.tg-inpop-answer { flex: 1; background: #059669; }
.tg-inpop-reject { background: #dc2626; padding-left: 16px; padding-right: 16px; }

/* dark softphone dialing panel — matches the real GlobalSoftphone widget */
.tg-soft { width: 100%; max-width: 240px; border-radius: 16px; overflow: hidden;
  box-shadow: 0 12px 40px -12px rgba(15,23,42,.35); animation: tg-pop .35s ease-out; }
.tg-soft-top { background: linear-gradient(to bottom, #1e293b, #0f172a); padding: 16px 16px 20px; text-align: center; }
.tg-soft-orb { width: 46px; height: 46px; margin: 0 auto 8px; border-radius: 999px; background: rgba(255,255,255,.1); display: grid; place-items: center; }
.tg-soft-num { font-size: 15px; font-weight: 700; color: #fff; font-variant-numeric: tabular-nums; }
.tg-soft-status { font-size: 11px; color: rgba(255,255,255,.7); margin-top: 3px; }
.tg-soft-controls { display: flex; align-items: center; justify-content: center; gap: 14px; padding: 13px; background: #fff; }
.tg-soft-mic { width: 40px; height: 40px; border-radius: 999px; border: 1px solid var(--line); background: var(--ground); color: var(--muted); display: grid; place-items: center; }
.tg-soft-hang { width: 44px; height: 44px; border-radius: 999px; background: #dc2626; display: grid; place-items: center; }

.tg-steps { display: flex; gap: 6px; justify-content: center; }
.tg-step { width: 20px; height: 4px; border-radius: 999px; background: var(--line); transition: background .3s; }
.tg-step--on { background: var(--accent); } .tg-step--done { background: var(--accent-soft); }

.tg-play { align-self: center; display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600;
  color: var(--accent); background: var(--accent-soft); border: 1px solid var(--accent-soft); border-radius: 999px; padding: 6px 14px; cursor: pointer; transition: background .15s; }

/* ── explanation ── */
.tg-explain { padding-top: 4px; }
.tg-explain-title { font-size: 14px; font-weight: 700; color: var(--slate); margin-bottom: 12px; text-wrap: balance; }
.tg-list { display: flex; flex-direction: column; gap: 4px; list-style: none; padding: 0; margin: 0; }
.tg-li { display: flex; gap: 11px; align-items: flex-start; padding: 9px 11px; border-radius: 9px; transition: background .25s; }
.tg-li--on { background: var(--accent-soft); }
.tg-li-num { flex-shrink: 0; width: 22px; height: 22px; border-radius: 999px; background: #e2e8f0; color: var(--muted);
  font-size: 11px; font-weight: 700; display: grid; place-items: center; transition: all .25s; }
.tg-li--on .tg-li-num { background: var(--accent); color: #fff; }
.tg-li-txt { font-size: 12.5px; color: #475569; line-height: 1.5; padding-top: 1px; }
.tg-li--on .tg-li-txt { color: var(--slate); }
.tg-note { margin-top: 14px; font-size: 11.5px; color: var(--muted); line-height: 1.55; padding: 10px 12px;
  background: var(--ground); border-radius: 9px; border-left: 3px solid var(--accent); }

@media (prefers-reduced-motion: reduce) {
  .tg-ring, .tg-orb--dial .tg-core, .tg-dot, .tg-progress > i, .tg-attract, .tg-bell-item--claim .tg-bell-cta, .tg-inpop-badge { animation: none !important; }
}
</style>
